import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContactCTA from '../../../components/home/ContactCTA';

const heroImage = 'https://www.intravis.com/hs-fs/hubfs/Products/Preforms/intravis-preform-inspection-production-hall-1920x780px.jpg?width=1920&height=780&name=intravis-preform-inspection-production-hall-1920x780px.jpg';
const productImage = 'https://www.intravis.com/hs-fs/hubfs/Products/Preforms/PreMon/intravis-preforms-premon-monitoring-system-mood-1920px.jpg?width=1920&height=1280&name=intravis-preforms-premon-monitoring-system-mood-1920px.jpg';
const preWatcherOfflineImage = 'https://www.intravis.com/hubfs/Products/Preforms/PreWatcher%20Offline/intravis-preform-prewatcher%20offline-system-product-1920x1280px.png';
const preWatcherInlineImage = 'https://www.intravis.com/hubfs/Products/Preforms/PreWatcher%20Inline/intravis-preform-prewatcher-inline-inspection-product-1920x1280px.png';
const preMonImage = 'https://www.intravis.com/hubfs/Products/Preforms/PreMon/intravis-preforms-premon-monitoring-system-with-command-station-1920px.jpg';

const PreformInspection = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 100);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  return (
    <>
      <section
        className="relative text-white h-56 sm:h-72 md:h-96 mt-20 sm:mt-12"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.45), rgba(15, 23, 42, 0.45)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/40 pointer-events-none" />
        <div className="container mx-auto px-4 h-full flex items-center">
          <div className="text-white">
            <h1 className="text-3xl md:text-4xl font-bold">Preform Inspection</h1>
            <p className="mt-2 max-w-2xl">High-speed vision systems for preform quality control, inline, offline, and lab inspection.</p>
            <div className="mt-6">
              <Link to="/products/intravis" className="text-blue-200 hover:text-white font-medium">
                ← Back to Inspection Systems
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Vision inspection for preforms</h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Intravis Preform Inspection systems deliver full-featured quality control for preform production. From thread and body measurement to cavity number readout, the systems inspect every preform for defects, dimensions and decoration before it reaches the molding line.
              </p>
              <p className="text-gray-700 mb-4 leading-relaxed">
                These turnkey inspection systems are designed for modern plastics production, offering both offline and inline operation with modular hardware and advanced image evaluation.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="bg-gray-50 p-6 rounded-xl shadow-sm">
                  <h3 className="text-xl font-semibold mb-3">Core Capabilities</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>Thread geometry and finish inspection</li>
                    <li>Gate and body surface defect detection</li>
                    <li>Color and contamination monitoring</li>
                    <li>Cavity number readout for traceability</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl shadow-sm">
                  <h3 className="text-xl font-semibold mb-3">Typical Benefits</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>Lower scrap rates through early defect detection</li>
                    <li>Higher throughput with automated preform sorting</li>
                    <li>Better process transparency with detailed reporting</li>
                    <li>Flexible integration into preform and bottling lines</li>
                  </ul>
                </div>
              </div>
            </div>
            <div>
              <img src={productImage} alt="Intravis Preform Inspection" className="w-full h-full rounded-3xl object-cover shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">Representative Intravis products</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="bg-white p-6 rounded-3xl shadow-md">
              <img src={preWatcherOfflineImage} alt="PreWatcher Offline" className="w-full h-44 object-cover rounded-3xl mb-5" />
              <h3 className="text-xl font-semibold mb-3">PreWatcher Offline</h3>
              <p className="text-gray-600 mb-4">Offline preform inspection with high-resolution imaging and fast cycle rates for batch quality control.</p>
              <div className="text-sm text-gray-600 space-y-2">
                <p>Designed for detailed cavity and thread inspection with fast setup.</p>
                <p>Benefits: accurate defect detection, reliable batch evaluation, easy integration.</p>
              </div>
              <div className="mt-4">
                <Link to="/contact" className="inline-block bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md text-sm">
                  Know More
                </Link>
              </div>
            </div>
            <div className="bg-white p-6 rounded-3xl shadow-md">
              <img src={preWatcherInlineImage} alt="PreWatcher Inline" className="w-full h-44 object-cover rounded-3xl mb-5" />
              <h3 className="text-xl font-semibold mb-3">PreWatcher Inline</h3>
              <p className="text-gray-600 mb-4">Inline inspection for immediate rejection of defective preforms directly in the production line.</p>
              <div className="text-sm text-gray-600 space-y-2">
                <p>Built for high-speed production with real-time quality control.</p>
                <p>Benefits: reduced scrap, faster throughput, consistent part quality.</p>
              </div>
              <div className="mt-4">
                <Link to="/contact" className="inline-block bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md text-sm">
                  Know More
                </Link>
              </div>
            </div>
            <div className="bg-white p-6 rounded-3xl shadow-md">
              <img src={preMonImage} alt="PreMon" className="w-full h-44 object-cover rounded-3xl mb-5" />
              <h3 className="text-xl font-semibold mb-3">PreMon</h3>
              <p className="text-gray-600 mb-4">Process monitoring system that detects quality drift and measures preform data for continuous improvement.</p>
              <div className="text-sm text-gray-600 space-y-2">
                <p>Designed to capture production data and support predictive quality control.</p>
                <p>Benefits: trend analysis, reduced process variability, stronger traceability.</p>
              </div>
              <div className="mt-4">
                <Link to="/contact" className="inline-block bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md text-sm">
                  Know More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA />
    </>
  );
};

export default PreformInspection;
